import { Injectable, Inject } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public currentTournament: Tournament;
  public tournaments: Tournament[] = [];
  public years: TournamentYear[] = [];

  public BYE_PITCH_NUM = 2147483647;
  
  public MOBILE_SCREEN_SIZE = 768;
  public mobile = false;

  protected httpHeaders: HttpHeaders = new HttpHeaders();
  protected httpOptions = {
    headers: this.httpHeaders
  }

  constructor(protected http: HttpClient, @Inject('BASE_API_URL') public baseUrl: string, public translate: TranslateService, public msg: MessageService) {
    this.httpHeaders = this.httpHeaders.append('Content-Type', 'application/json');
    this.httpHeaders = this.httpHeaders.append('Cache-Control', 'no-cache');
    this.httpHeaders = this.httpHeaders.append('Pragma', 'no-cache');
  }

  public getFieldName(type: string): string {
    switch (type) {
      case 'PITCH': return this.translate.instant('FIELDTYPES.PITCH'); break;
	    case 'FIELD': return this.translate.instant('FIELDTYPES.FIELD'); break;
	    case 'COURT': return this.translate.instant('FIELDTYPES.COURT'); break;
	    case 'TABLE': return this.translate.instant('FIELDTYPES.TABLE'); break;
      case 'BOARD': return this.translate.instant('FIELDTYPES.BOARD'); break;
      default: return this.translate.instant('FIELDTYPES.PITCH');
    };
  }

  public setMobile(): void {
    this.mobile = false;
    if (window.screen.width < this.MOBILE_SCREEN_SIZE) { 
      this.mobile = true;
    }
  }  

  // Data provider methods
  public getLeaders(bracketLevel: number, round: number, callback?: (data: Leader[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<Leader[]>(this.baseUrl + '/getLeaders/' + this.currentTournament.transferPK + '/' + bracketLevel + '/' + round).subscribe(
      result => {
        this.currentTournament.leaders = result;
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTeamPlayers(team: TournamentTeam, callback?: (data: TeamPlayer[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<TeamPlayer[]>(this.baseUrl + '/getTeamPlayers/' + this.currentTournament.transferPK + '/' + team.id, this.httpOptions).subscribe(
      result => {
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTournamentBrackets(callback?: (data: Bracket[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<Bracket[]>(this.baseUrl + '/getTournamentBrackets/' + this.currentTournament.transferPK, this.httpOptions).subscribe(
      result => {
        this.currentTournament.brackets = result;
        if (callback) callback(result);
      }, 
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTournamentMatchResults(callback?: (data: MatchResult[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<MatchResult[]>(this.baseUrl + '/getTournamentMatchResults/' + this.currentTournament.transferPK, this.httpOptions).subscribe(
      result => {
        if (result.length > 0) {
          let i = 0;
          let bracket = null;
          let round = null;
          let match = null;
          let team = null;
          while (i < result.length) {
            if (this.isNullOrUndefined(bracket) || bracket.id !== result[i].bracket) {
              bracket = this.currentTournament.brackets.find(b => b.id === result[i].bracket);
            }
            if (this.isNullOrUndefined(bracket)) { continue; }
            if (this.isNullOrUndefined(bracket.rounds)) { bracket.rounds = []; }

            if (this.isNullOrUndefined(round) || result[i].bracket !== bracket.id || result[i].round !== round.roundNum) {
              round = bracket.rounds.find(r => r.roundNum === result[i].round);
            } 
            if (this.isNullOrUndefined(round)) {
              match = null;
              team = null;
              round = new BracketRound();
              round.bracketId = result[i].bracket;
              round.roundNum = result[i].round;
              round.rank = result[i].roundRank;
              round.matchType = result[i].matchType;
              if (result[i].matchType === 'DMK') {
                round.name = this.translate.instant('TOURNAMENTS.ROUND') + ' ' + result[i].round;
              } else {
                if (result[i].round === 0) {
                  round.name = this.translate.instant('TOURNAMENTS.ELIMINATIONROUND');
                } else {
                  let roundDiff = -1;
                  if (bracket.hasElimRound) {
                    roundDiff = (bracket.numRounds - 1) - result[i].round;
                  } else {
                    roundDiff = (bracket.numRounds) - result[i].round;
                  }
      
                  switch (roundDiff) {
                    case 0: round.name = 'FINALS'; break; // Need to determine based on match
                    case 1: round.name = this.translate.instant('TOURNAMENTS.SEMIFINALS'); break;
                    case 2: round.name = this.translate.instant('TOURNAMENTS.QUARTERFINALS'); break;
                    case 3: round.name = this.translate.instant('TOURNAMENTS.ROUNDOF') + ' 16'; break;
                    case 4: round.name = this.translate.instant('TOURNAMENTS.ROUNDOF') + ' 32'; break;
                    case 5: round.name = this.translate.instant('TOURNAMENTS.ROUNDOF') + ' 64'; break;
                    case 6: round.name = this.translate.instant('TOURNAMENTS.ROUNDOF') + ' 128'; break;
                    default: round.name = this.translate.instant('TOURNAMENTS.ROUND') + ' ' + result[i].round; break;
                  }
                }
              }
              bracket.rounds.push(round);
            }
            
            if (this.isNullOrUndefined(match) || round.roundNum !== result[i].round || match.id !== result[i].matchId) {
              match = round.matches.find(m => m.id === result[i].matchId);
            } 
            if (this.isNullOrUndefined(match)) {
              team = null;
              match = new Match();
              match.id = result[i].matchId;
              match.number = result[i].matchNum;
              match.bracket = result[i].bracket;
              match.pitchNum = result[i].pitchNum;
              match.pitchName = result[i].pitchName ? result[i].pitchName : this.getFieldName(this.currentTournament.fieldName) + ' ' + match.pitchNum;
              match.type = result[i].matchType;
              match.numGames = result[i].numGames;
              match.complete = result[i].complete;
              match.round = result[i].round;
              match.roundRank = result[i].roundRank;
              match.bye = match.pitchNum === this.BYE_PITCH_NUM ? true : false;
              round.matches.push(match);
            }

            if (result[i].team < 0) { //make sure the games get split evenly for a match of teams have not been assigned
              if (result[i].gameId % 2 === 1) {
                result[i].team = -2;
              }
            }
            if (this.isNullOrUndefined(team) || team.id !== result[i].team || round.roundNum !== result[i].round || match.id !== result[i].matchId) {
              team = match.teams.find(t => t.id === result[i].team);
            }
            if (this.isNullOrUndefined(team)) {
              team = new TournamentTeam();
              team.id = result[i].team;
              team.name = result[i].teamName;
              team.rank = result[i].teamRank;
              team.score = 0;
              team.eliminationBye = result[i].eliminationBye;
              team.bracketId = result[i].teamBracketId;
              match.teams.push(team);
            }

            let game = new Game();
            game.id = result[i].gameId;
            game.number = result[i].gameNumber;
            game.score = result[i].score;
            game.scoreMeaning = result[i].scoreMeaning;
            game.baseKubbsHit = result[i].baseKubbsHit;
            team.games.push(game);

            team.score += game.score;

            i++;
          }
        }
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTeamTournamentSummary(teamId: number, callback?: (data: TeamResult[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<TeamResult[]>(this.baseUrl + '/getTeamTournamentSummary/' + this.currentTournament.transferPK + '/' + teamId, this.httpOptions).subscribe(
      result => {
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTournamentsByYear(year: TournamentYear, callback?: (data: Tournament[]) => void, errorCallback?: (error: unknown) => void) : void {
    this.http.get<Tournament[]>(this.baseUrl + '/getTournamentsByYear/' + year.year, this.httpOptions).subscribe(
      result => {
        this.tournaments = result;
        console.log(this.tournaments);
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  public getTournamentTeams(callback?: (data: TournamentTeamResult[]) => void, errorCallback?: (error: unknown) => void) {
    this.http.get<TournamentTeamResult[]>(this.baseUrl + '/getTournamentTeams/' + this.currentTournament.transferPK, this.httpOptions).subscribe(
      result => {
        let team = null;
        this.currentTournament.teams = [];
        result.forEach(t => {
          if (this.isNullOrUndefined(team) || team.id !== t.id) {
            team = this.currentTournament.teams.find(x => x.id === t.id);
          } 
          if (this.isNullOrUndefined(team)) {
            team = new TournamentTeam();
            team.id = t.id;
            team.active = t.active;
            team.rank = t.rank;
            team.name = t.id > 0 ? t.name : '';
            team.eliminationBye = t.eliminationBye;
            team.bracketId = t.bracketId;
            team.players = [];
            this.currentTournament.teams.push(team);
          }

          let player = new TeamPlayer();
          player.firstName = t.firstName;
          player.lastName = t.lastName;
          player.middleName = t.middleName;
          player.city = t.city;
          player.state = t.state;
          player.captain = t.captain;
          team.players.push(player);
        });

        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    )
  }

  public getTournamentYears(callback?: (data: TournamentYear[]) => void, errorCallback?: (error: unknown) => void): void {
    this.http.get<TournamentYear[]>(this.baseUrl + '/getTournamentYears', this.httpOptions).subscribe(
      result => {
        this.years = result;
        if (callback) callback(result);
      },
      error => {
        this.Error(error);
        if (errorCallback) errorCallback(error);
      }
    );
  }

  // private
  private BreakableUrl(url: string): string {
    if (!url || url === "")
        return "";
    else
        return url.replace(/\//g, "<wbr>/").replace(/&/g, "<wbr>&");
  }

  private Error(error: any): void {
    console.log(error);
    this.msg.add({ severity: "error", summary: this.translate.instant("CONFIG.SERVEREXCEPTION"), detail: "URL: " + this.BreakableUrl(error.url) });
  }

  public isNullOrUndefined(o: any): boolean {
    return o == null || o == undefined; 
  }
}

export class Bracket {
  tournamentKey: number;
  id: number;
  level: number;
  bracketName: string;
  format: string;
  matchLength: string;
  timeLimit: number;
  numTeams: string;
  pitchAssign: string;
  sendToConsolation: boolean;
  numRounds: number;
  bracketOrder: number;
  teamsWithByes: number;
  hasElimRound: boolean;
  rounds: BracketRound[] = [];
  maxCompleteRound: number;
  expanded = false; // used for menu display
}

export class BracketRound {
  bracketId: number;
  roundNum: number;
  matchType: number;
  name: string;
  rank: number;
  matches: Match[] = [];
}

export class Game {
  id: number;
  number: number;
  score: number;
  scoreMeaning: string;
  baseKubbsHit: number;
}

export class Match {
  id: number;
  bracket: number;
  round: number;
  roundRank: number;
  pitchNum: number;
  type: string;
  numGames: number;
  complete: number;
  bye: boolean;
  teams: TournamentTeam[] = [];
  showHeaders: boolean;
}

export class MatchResult {
  tournamentKey: number;
  matchId: number;
  matchNum: number;
  bracket: number;
  round: number;
  pitchNum: number;
  pitchName: string;
  matchType: string;
  numGames: number;
  complete: boolean;
  roundRank: number;
  gameId: number;
  gameNumber: number;
  team: number;
  teamName: string;
  score: number;
  scoreMeaning: string;
  teamRank: number;
  eliminationBye: boolean;
  teamBracketId: number;
  baseKubbsHit: number;
}

export class Leader {
  tournamentKey: number;
  bracket: number;
  id: number;
  name: string;
  score: number;
  opponentScore: number;
  opponentopponentScore: number;
}

export class TeamPlayer {
  id: number;
  firstName: string;
  lastName: string;
  middleName: string;
  city: string;
  state: string;
  captain: boolean;
  teamName: string;
}

export class TeamResult {
  bracket: number;
  bracketName: string;
  numberOfRounds: number;
  matchType: string;
  round: number;
  score: number;
  scoreMeaning: string;
  rank: number;
  opponent: number;
  opponentName: string;
  pitchNum: number;
  complete: boolean;
}

export class Tournament {
  transferPK: string;
  id: number;
  name: string;
  day: number;
  month: number;
  year: number;
  teamSize: number;
  location: string;
  director: string;
  logo: string;
  allowVariableTeamSizes: boolean;
  startDate: Date;
  endDate: Date;
  isTournament: boolean;
  isTeamEvent: boolean;
  inProgress: boolean;
  shared: boolean;
  fieldName: string;
  teams: TournamentTeam[] = [];
  leaders: Leader[] = [];
  brackets: Bracket[];
}

export class TournamentTeam {
  id: number;
  active: boolean;
  rank: number;
  name: string;
  score: number;
  eliminationBye: boolean;
  bracketId: number;
  games: Game[] = [];
  players: TeamPlayer[] = [];
}

export class TournamentTeamResult {
  id: number;
  active: boolean; 
  rank: number; 
  name: string;
  eliminationBye: boolean;
  bracketId: number;
	firstName: string;
	lastName: string;
	middleName: string;
  city: string;
	state: string;
	captain: boolean;
}

export class TournamentYear {
  year: number;
}